import * as React from 'react'
import {
    Box,
    Collapse,
    Grid,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CastleRoundedIcon from '@mui/icons-material/CastleRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import UnitIconFrame from './UnitIconFrame'

const AREA_TYPE_LABELS = Object.freeze({
    1: 'Main Castle',
    3: 'Capital',
    4: 'Outpost',
    12: 'Castle',
    22: 'Metro'
})

const KINGDOM_LABELS = Object.freeze({
    0: 'Great Empire',
    1: 'Burning Sands',
    2: 'Everwinter Glacier',
    3: 'Fire Peaks',
    4: 'Storm Islands',
    10: 'Berimond'
})

const KINGDOM_SORT_ORDER = Object.freeze([0, 2, 1, 3, 4, 10])

const FALLBACK_LOCATIONS = Object.freeze([
    { key: 'fallback-main', label: 'Main Castle', kingdomLabel: 'Great Empire', typeLabel: 'Main Castle', scope: 'main', sortIndex: 0 },
    { key: 'fallback-outpost-1', label: 'Outpost 1', kingdomLabel: 'Great Empire', typeLabel: 'Outpost', scope: 'main', sortIndex: 1 },
    { key: 'fallback-outpost-2', label: 'Outpost 2', kingdomLabel: 'Great Empire', typeLabel: 'Outpost', scope: 'main', sortIndex: 2 },
    { key: 'fallback-outpost-3', label: 'Outpost 3', kingdomLabel: 'Great Empire', typeLabel: 'Outpost', scope: 'main', sortIndex: 3 },
    { key: 'fallback-ice', label: 'Ice Castle', kingdomLabel: 'Everwinter Glacier', typeLabel: 'Castle', scope: 'main', sortIndex: 4 },
    { key: 'fallback-desert', label: 'Desert Castle', kingdomLabel: 'Burning Sands', typeLabel: 'Castle', scope: 'main', sortIndex: 5 },
    { key: 'fallback-fire', label: 'Fire Castle', kingdomLabel: 'Fire Peaks', typeLabel: 'Castle', scope: 'main', sortIndex: 6 },
    { key: 'fallback-storm', label: 'Storm Castle', kingdomLabel: 'Storm Islands', typeLabel: 'Castle', scope: 'main', sortIndex: 7 },
    { key: 'fallback-capital', label: 'Capital', kingdomLabel: 'Great Empire', typeLabel: 'Capital', scope: 'main', sortIndex: 8 },
    { key: 'fallback-metro', label: 'Metro', kingdomLabel: 'Great Empire', typeLabel: 'Metro', scope: 'main', sortIndex: 9 }
])

const SESSION_HANDOFF_RECONNECT_OPTIONS = Object.freeze(['0', '1', '2', '5', '10', '15', '30', '60'])

const toFiniteNumber = value => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeNonNegativeNumber = value => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0
}

const formatCompactNumber = value => {
    const amount = normalizeNonNegativeNumber(value)
    if (amount >= 1000000000)
        return `${Number((amount / 1000000000).toFixed(amount >= 10000000000 ? 0 : 1))}b`
    if (amount >= 1000000)
        return `${Number((amount / 1000000).toFixed(amount >= 10000000 ? 0 : 1))}m`
    if (amount >= 1000)
        return `${Number((amount / 1000).toFixed(amount >= 100000 ? 0 : 1))}k`

    return String(amount)
}

const normalizeInventoryItem = (item, itemType = 'troop') => {
    const unitID = toFiniteNumber(item?.unitID)
    const amount = normalizeNonNegativeNumber(item?.amount)
    if (!amount)
        return undefined

    return {
        unitID,
        amount,
        label: String(item?.label ?? (unitID !== undefined ? `Unit ${unitID}` : itemType)).trim() || itemType,
        iconUrl: String(item?.iconUrl ?? '').trim(),
        assetKey: String(item?.assetKey ?? '').trim(),
        itemLevel: toFiniteNumber(item?.itemLevel ?? item?.toolLevel ?? item?.troopLevel ?? item?.level),
        troopLevel: toFiniteNumber(item?.troopLevel),
        role: String(item?.role ?? '').trim(),
        side: String(item?.side ?? '').trim(),
        itemType
    }
}

const normalizeDefenseSetupItem = (item, section) => {
    const normalized = normalizeInventoryItem(item, 'tool')
    if (!normalized)
        return undefined

    return {
        ...normalized,
        section,
        position: String(item?.position ?? '').trim(),
        positionLabel: String(item?.positionLabel ?? item?.position ?? 'Slot').trim() || 'Slot',
        slotIndex: toFiniteNumber(item?.slotIndex)
    }
}

const sortDefenseSetupItems = (left, right) => {
    const positionOrder = { left: 0, middle: 1, right: 2 }
    const leftPosition = positionOrder[String(left.position ?? '').toLowerCase()] ?? 99
    const rightPosition = positionOrder[String(right.position ?? '').toLowerCase()] ?? 99

    return leftPosition - rightPosition ||
        Number(left.slotIndex ?? Number.MAX_SAFE_INTEGER) - Number(right.slotIndex ?? Number.MAX_SAFE_INTEGER) ||
        String(left.label).localeCompare(String(right.label)) ||
        Number(left.unitID ?? Number.MAX_SAFE_INTEGER) - Number(right.unitID ?? Number.MAX_SAFE_INTEGER)
}

const normalizeDefenseSetup = setup => ({
    updatedAt: toFiniteNumber(setup?.updatedAt),
    wall: (Array.isArray(setup?.wall) ? setup.wall : [])
        .map(item => normalizeDefenseSetupItem(item, 'wall'))
        .filter(Boolean)
        .sort(sortDefenseSetupItems),
    moat: (Array.isArray(setup?.moat) ? setup.moat : [])
        .map(item => normalizeDefenseSetupItem(item, 'moat'))
        .filter(Boolean)
        .sort(sortDefenseSetupItems)
})

const sortInventoryItems = (left, right) =>
    Number(right.amount) - Number(left.amount) ||
    String(left.label).localeCompare(String(right.label)) ||
    Number(left.unitID ?? Number.MAX_SAFE_INTEGER) - Number(right.unitID ?? Number.MAX_SAFE_INTEGER)

const formatCoordinate = (x, y) =>
    Number.isFinite(Number(x)) && Number.isFinite(Number(y)) ? `${Number(x)}:${Number(y)}` : ''

const buildLocationKey = (kingdomID, areaID, fallback) =>
    kingdomID !== undefined && areaID !== undefined ? `${kingdomID}:${areaID}` : fallback

const resolveLocationScope = location => {
    const kingdomID = toFiniteNumber(location?.kingdomID)
    const kingdomLabel = String(location?.kingdomLabel ?? '').toLowerCase()

    if (kingdomID !== undefined && kingdomID > 10)
        return 'outer'

    if (/\bouter\b|\bbth\b|battle for the throne/i.test(kingdomLabel))
        return 'outer'

    return 'main'
}

const normalizeTroopCastle = castle => {
    const kingdomID = toFiniteNumber(castle?.kingdomID)
    const areaID = toFiniteNumber(castle?.areaID)
    const key = buildLocationKey(kingdomID, areaID, `stock-${String(castle?.castleLabel ?? '').trim()}`)
    const units = (Array.isArray(castle?.units) ? castle.units : [])
        .map(item => normalizeInventoryItem(item, 'troop'))
        .filter(Boolean)
        .sort(sortInventoryItems)
    const tools = (Array.isArray(castle?.tools) ? castle.tools : [])
        .map(item => normalizeInventoryItem(item, 'tool'))
        .filter(Boolean)
        .sort(sortInventoryItems)
    const defenseSetup = normalizeDefenseSetup(castle?.defenseSetup)

    return {
        key,
        kingdomID,
        areaID,
        type: toFiniteNumber(castle?.areaType),
        label: String(castle?.castleLabel ?? '').trim() || 'Unknown castle',
        kingdomLabel: KINGDOM_LABELS[kingdomID] ?? (kingdomID !== undefined ? `Kingdom ${kingdomID}` : 'Unknown kingdom'),
        typeLabel: AREA_TYPE_LABELS[toFiniteNumber(castle?.areaType)] ?? 'Castle',
        detail: formatCoordinate(castle?.x, castle?.y),
        troopTotal: normalizeNonNegativeNumber(castle?.total),
        toolTotal: normalizeNonNegativeNumber(castle?.toolTotal),
        toolTypes: normalizeNonNegativeNumber(castle?.toolTypes),
        attack: normalizeNonNegativeNumber(castle?.attack),
        defense: normalizeNonNegativeNumber(castle?.defense),
        stronghold: normalizeNonNegativeNumber(castle?.stronghold),
        hospital: normalizeNonNegativeNumber(castle?.hospital),
        traveling: normalizeNonNegativeNumber(castle?.traveling),
        units,
        tools,
        defenseSetup,
        source: 'stock'
    }
}

const normalizeBarracksTarget = target => {
    const kingdomID = toFiniteNumber(target?.kingdomID)
    const areaID = toFiniteNumber(target?.areaID)
    const key = buildLocationKey(kingdomID, areaID, String(target?.key ?? `target-${target?.label ?? ''}`))
    const detail = String(target?.detail ?? '').trim() || formatCoordinate(target?.x, target?.y)

    return {
        key,
        kingdomID,
        areaID,
        type: toFiniteNumber(target?.type),
        label: String(target?.label ?? target?.nameCandidate ?? '').trim() || 'Unknown castle',
        kingdomLabel: String(target?.kingdomLabel ?? '').trim() || KINGDOM_LABELS[kingdomID] || (kingdomID !== undefined ? `Kingdom ${kingdomID}` : 'Unknown kingdom'),
        typeLabel: String(target?.typeLabel ?? '').trim() || AREA_TYPE_LABELS[toFiniteNumber(target?.type)] || 'Castle',
        detail,
        barracksLevel: toFiniteNumber(target?.barracksLevel),
        barracksState: String(target?.barracksState ?? '').trim(),
        defaultSlotSize: toFiniteNumber(target?.defaultSlotSize),
        troopTotal: 0,
        toolTotal: 0,
        toolTypes: 0,
        attack: 0,
        defense: 0,
        stronghold: 0,
        hospital: 0,
        traveling: 0,
        units: [],
        tools: [],
        defenseSetup: normalizeDefenseSetup(),
        source: 'target'
    }
}

export const buildCoreLocationOverview = status => {
    const byKey = new Map()
    const targets = Array.isArray(status?.barracksTargets) ? status.barracksTargets : []
    const stockCastles = Array.isArray(status?.troopStock?.castles) ? status.troopStock.castles : []

    targets.map(normalizeBarracksTarget).forEach((target, index) => {
        byKey.set(target.key, {
            ...target,
            sortIndex: index
        })
    })

    stockCastles.map(normalizeTroopCastle).forEach((castle, index) => {
        const previous = byKey.get(castle.key)
        byKey.set(castle.key, {
            ...castle,
            ...previous,
            label: previous?.label && previous.label !== 'Unknown castle' ? previous.label : castle.label,
            kingdomLabel: previous?.kingdomLabel ?? castle.kingdomLabel,
            typeLabel: previous?.typeLabel ?? castle.typeLabel,
            detail: previous?.detail || castle.detail,
            troopTotal: castle.troopTotal,
            toolTotal: castle.toolTotal,
            toolTypes: castle.toolTypes,
            attack: castle.attack,
            defense: castle.defense,
            stronghold: castle.stronghold,
            hospital: castle.hospital,
            traveling: castle.traveling,
            units: castle.units,
            tools: castle.tools,
            defenseSetup: castle.defenseSetup,
            sortIndex: previous?.sortIndex ?? targets.length + index
        })
    })

    const liveLocations = Array.from(byKey.values()).map(location => ({
        ...location,
        scope: resolveLocationScope(location)
    }))

    const locations = liveLocations.length > 0 ?
        liveLocations :
        FALLBACK_LOCATIONS.map(location => ({
            ...location,
            troopTotal: 0,
            attack: 0,
            defense: 0,
            stronghold: 0,
            hospital: 0,
            traveling: 0,
            toolTotal: 0,
            toolTypes: 0,
            units: [],
            tools: [],
            defenseSetup: normalizeDefenseSetup(),
            barracksState: 'not-synced'
        }))

    locations.sort((left, right) => {
        const leftKingdomIndex = KINGDOM_SORT_ORDER.indexOf(Number(left.kingdomID))
        const rightKingdomIndex = KINGDOM_SORT_ORDER.indexOf(Number(right.kingdomID))
        const normalizedLeftKingdomIndex = leftKingdomIndex >= 0 ? leftKingdomIndex : Number.MAX_SAFE_INTEGER
        const normalizedRightKingdomIndex = rightKingdomIndex >= 0 ? rightKingdomIndex : Number.MAX_SAFE_INTEGER

        return normalizedLeftKingdomIndex - normalizedRightKingdomIndex ||
            Number(left.sortIndex ?? Number.MAX_SAFE_INTEGER) - Number(right.sortIndex ?? Number.MAX_SAFE_INTEGER) ||
            String(left.label).localeCompare(String(right.label))
    })

    return {
        locations,
        mainCount: locations.filter(location => location.scope !== 'outer').length,
        outerCount: locations.filter(location => location.scope === 'outer').length,
        hasLiveData: liveLocations.length > 0,
        updatedAt: toFiniteNumber(status?.troopStock?.updatedAt)
    }
}

function SectionHeader({ title }) {
    return (
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ pt: 1 }}>
            <Typography variant="h5" sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                {title}
            </Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.18)' }} />
        </Stack>
    )
}

function SettingField({ label, value, onChange, helper, required = false, options }) {
    const normalizedOptions = Array.isArray(options) ? options : []
    const currentValue = String(value ?? '')
    const selectOptions = normalizedOptions.some(option => String(option.value) === currentValue) || !currentValue ?
        normalizedOptions :
        [...normalizedOptions, { value: currentValue, label: currentValue }]

    return (
        <Stack spacing={0.75}>
            <Typography variant="body1" sx={{ color: '#f5f7ff', fontWeight: 700 }}>
                {label}
                {required && (
                    <Box component="span" sx={{ color: '#ff6b6b', ml: 0.5 }}>
                        *
                    </Box>
                )}
            </Typography>
            <TextField
                fullWidth
                value={value}
                onChange={event => onChange(event.target.value)}
                size="small"
                select={selectOptions.length > 0}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(12, 23, 40, 0.88)',
                        color: '#f5f7ff',
                        '& fieldset': {
                            borderColor: 'rgba(129, 146, 178, 0.26)'
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(143, 180, 255, 0.44)'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#8fb4ff'
                        }
                    },
                    '& .MuiInputBase-input': {
                        color: '#f5f7ff',
                        fontWeight: 700
                    }
                }}
            >
                {selectOptions.map(option => (
                    <MenuItem key={String(option.value)} value={String(option.value)}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#9fb0cc' }} />
                <Typography variant="caption" sx={{ color: '#9fb0cc', lineHeight: 1.45 }}>
                    {helper}
                </Typography>
            </Stack>
        </Stack>
    )
}

const resolveLocationTone = location => {
    if (Number(location.kingdomID) === 4)
        return { color: '#3762c7', labelColor: '#4f78ff' }
    if (Number(location.kingdomID) === 2)
        return { color: '#246c9e', labelColor: '#4fa7da' }
    if (Number(location.kingdomID) === 1)
        return { color: '#93602a', labelColor: '#d6a046' }
    if (Number(location.kingdomID) === 3)
        return { color: '#9c3b31', labelColor: '#db6559' }
    if (location.scope === 'outer')
        return { color: '#6f4bb0', labelColor: '#b492ff' }

    return { color: '#426e2f', labelColor: '#6fb453' }
}

function CoreInventoryItem({ item, itemType, caption, t }) {
    const amountLabel = formatCompactNumber(item.amount)
    const captionLabel = String(caption ?? '').trim()
    const itemLevel = toFiniteNumber(item.itemLevel ?? item.troopLevel)
    const levelLabel = `${t('Level')} ${itemLevel !== undefined ? Math.floor(itemLevel) : '—'}`

    return (
        <Box
            title={`${captionLabel ? `${captionLabel} - ` : ''}${item.label} - ${levelLabel} - ${amountLabel}`}
            sx={{
                width: 110,
                minHeight: captionLabel ? 142 : 126,
                borderRadius: '8px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                backgroundColor: 'rgba(12, 23, 40, 0.78)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                overflow: 'hidden',
                flexShrink: 0,
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.025)'
            }}
        >
            <UnitIconFrame
                iconUrl={item.iconUrl}
                itemType={itemType}
                level={itemLevel}
                size={75}
            />
            <Typography
                sx={{
                    width: '100%',
                    minHeight: 28,
                    px: 0.45,
                    color: '#dbe6f8',
                    fontWeight: 800,
                    fontSize: '0.76rem',
                    lineHeight: 1.15,
                    textAlign: 'center',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                    overflowWrap: 'anywhere'
                }}
            >
                {item.label}
            </Typography>
            <Typography
                sx={{
                    width: '100%',
                    px: 0.45,
                    color: itemLevel !== undefined ? '#8fc6ff' : '#8290a8',
                    fontWeight: 900,
                    fontSize: '0.65rem',
                    lineHeight: 1.05,
                    textAlign: 'center'
                }}
            >
                {levelLabel}
            </Typography>
            {captionLabel ? (
                <Typography
                    sx={{
                        width: '100%',
                        px: 0.45,
                        color: '#8fb4ff',
                        fontWeight: 900,
                        fontSize: '0.62rem',
                        lineHeight: 1,
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        overflowWrap: 'anywhere'
                    }}
                >
                    {captionLabel}
                </Typography>
            ) : null}
            <Typography
                sx={{
                    width: '100%',
                    minHeight: 22,
                    px: 0.45,
                    pb: 0.45,
                    color: '#f5f7ff',
                    fontWeight: 900,
                    fontSize: '0.72rem',
                    lineHeight: 1.05,
                    textAlign: 'center',
                    textShadow: '0 1px 1px rgba(0,0,0,0.55)',
                    overflowWrap: 'anywhere'
                }}
            >
                {amountLabel}
            </Typography>
        </Box>
    )
}

function CoreInventorySection({ title, items, itemType, t }) {
    if (!items.length)
        return null

    return (
        <Box>
            <Typography
                sx={{
                    color: '#8fb4ff',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    letterSpacing: 0,
                    textTransform: 'uppercase'
                }}
            >
                {`${t(title)} - ${items.length}`}
            </Typography>
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
                {items.map((item, index) => (
                    <CoreInventoryItem
                        key={`${itemType}-${index}-${item.unitID ?? item.assetKey ?? item.label}`}
                        item={item}
                        itemType={itemType}
                        t={t}
                    />
                ))}
            </Stack>
        </Box>
    )
}

function CoreDefenseSetupSection({ title, items, t }) {
    if (!items.length)
        return null

    return (
        <Box>
            <Typography
                sx={{
                    color: '#8fb4ff',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    letterSpacing: 0,
                    textTransform: 'uppercase'
                }}
            >
                {`${t(title)} - ${items.length}`}
            </Typography>
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
                {items.map((item, index) => (
                    <CoreInventoryItem
                        key={`${title}-${item.position}-${item.slotIndex ?? index}-${item.unitID ?? item.assetKey ?? item.label}`}
                        item={item}
                        itemType="tool"
                        caption={t(item.positionLabel)}
                        t={t}
                    />
                ))}
            </Stack>
        </Box>
    )
}

function CoreLocationCard({ location, t }) {
    const units = Array.isArray(location.units) ? location.units : []
    const tools = Array.isArray(location.tools) ? location.tools : []
    const wallTools = Array.isArray(location.defenseSetup?.wall) ? location.defenseSetup.wall : []
    const moatTools = Array.isArray(location.defenseSetup?.moat) ? location.defenseSetup.moat : []
    const hasInventory = units.length > 0 || tools.length > 0
    const hasDefenseSetup = wallTools.length > 0 || moatTools.length > 0
    const hasDefenseSync = Boolean(location.defenseSetup?.updatedAt)
    const troopTotal = normalizeNonNegativeNumber(location.troopTotal)
    const toolTypes = normalizeNonNegativeNumber(location.toolTypes ?? tools.length)
    const tone = resolveLocationTone(location)
    const toneColor =
        tone.color

    return (
        <Box
            sx={{
                borderRadius: '8px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                backgroundColor: 'rgba(16, 27, 46, 0.44)',
                overflow: 'hidden',
                minHeight: hasInventory || hasDefenseSetup ? 354 : 236,
                boxShadow: '0 16px 34px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                    px: 1.8,
                    py: 1.25,
                    backgroundColor: `${toneColor}24`,
                    borderBottom: '1px solid rgba(129, 146, 178, 0.18)'
                }}
            >
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${toneColor}88`,
                        backgroundColor: `${toneColor}24`,
                        flexShrink: 0
                    }}
                >
                    <CastleRoundedIcon sx={{ color: '#f5f7ff', fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#f5f7ff', fontWeight: 900, fontSize: '1rem', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                        {location.label}
                    </Typography>
                    <Typography sx={{ mt: 0.32, color: tone.labelColor, fontSize: '0.78rem', fontWeight: 700 }}>
                        {location.kingdomLabel}
                    </Typography>
                </Box>
            </Stack>

            <Box sx={{ p: 1.45 }}>
                {hasInventory || hasDefenseSetup || hasDefenseSync ? (
                    <Stack
                        spacing={1.15}
                    >
                        {hasInventory ? (
                            <Stack
                                spacing={1.1}
                                sx={{
                                    p: 1.2,
                                    borderRadius: '8px',
                                    border: '1px solid rgba(129, 146, 178, 0.16)',
                                    backgroundColor: 'rgba(7, 15, 28, 0.55)'
                                }}
                            >
                                <Typography sx={{ color: '#8fb4ff', fontSize: '0.74rem', fontWeight: 900 }}>
                                    {`${t('In castle')} ${formatCompactNumber(troopTotal)} ${t('troops')} - ${toolTypes} ${t('tool types')}`}
                                </Typography>
                                <CoreInventorySection title="TROOPS" items={units} itemType="troop" t={t} />
                                <CoreInventorySection title="TOOLS" items={tools} itemType="tool" t={t} />
                            </Stack>
                        ) : null}
                        {hasDefenseSetup ? (
                            <Stack
                                spacing={1.1}
                                sx={{
                                    p: 1.2,
                                    borderRadius: '8px',
                                    border: '1px solid rgba(129, 146, 178, 0.16)',
                                    backgroundColor: 'rgba(7, 15, 28, 0.55)'
                                }}
                            >
                                <Typography sx={{ color: '#8fb4ff', fontSize: '0.74rem', fontWeight: 900 }}>
                                    {t('Defense setup')}
                                </Typography>
                                <CoreDefenseSetupSection title="WALL SETUP" items={wallTools} t={t} />
                                <CoreDefenseSetupSection title="MOAT SETUP" items={moatTools} t={t} />
                            </Stack>
                        ) : hasDefenseSync ? (
                            <Typography sx={{ color: '#9fb0cc', fontSize: '0.82rem', fontStyle: 'italic' }}>
                                {t('No wall or moat tools equipped.')}
                            </Typography>
                        ) : null}
                    </Stack>
                ) : (
                    <Typography sx={{ color: '#9fb0cc', fontSize: '0.82rem', fontStyle: 'italic' }}>
                        {t('No troops or tools.')}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

function CoreLocationListItem({ location, selected, onSelect, t }) {
    const tone = resolveLocationTone(location)
    const troopTotal = normalizeNonNegativeNumber(location.troopTotal)

    return (
        <Box
            component="button"
            type="button"
            aria-pressed={selected}
            onClick={onSelect}
            sx={{
                width: '100%',
                p: 1.05,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: '8px',
                border: selected ? `1px solid ${tone.color}cc` : '1px solid rgba(129, 146, 178, 0.16)',
                backgroundColor: selected ? `${tone.color}2e` : 'rgba(7, 15, 28, 0.38)',
                color: '#f5f7ff',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
                '&:hover': {
                    borderColor: `${tone.color}aa`,
                    backgroundColor: `${tone.color}20`
                },
                '&:focus-visible': {
                    outline: '2px solid #8fb4ff',
                    outlineOffset: 2
                },
                '&:active': {
                    transform: 'translateY(1px)'
                }
            }}
        >
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${tone.color}88`,
                    backgroundColor: `${tone.color}24`
                }}
            >
                <CastleRoundedIcon sx={{ color: '#f5f7ff', fontSize: 10 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: '#f5f7ff', fontSize: '0.86rem', fontWeight: 900, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                    {location.label}
                </Typography>
                <Typography sx={{ mt: 0.25, color: tone.labelColor, fontSize: '0.7rem', fontWeight: 700 }}>
                    {location.kingdomLabel}
                </Typography>
            </Box>
            <Typography sx={{ color: '#9fb0cc', fontSize: '0.68rem', fontWeight: 800, flexShrink: 0 }}>
                {`${formatCompactNumber(troopTotal)} ${t('troops')}`}
            </Typography>
        </Box>
    )
}

export function CoreLocationsPanel({ runtimeStatus, t }) {
    const overview = React.useMemo(() => buildCoreLocationOverview(runtimeStatus), [runtimeStatus])
    const [selectedLocationKey, setSelectedLocationKey] = React.useState('')
    const updatedLabel = overview.updatedAt ? new Date(overview.updatedAt).toLocaleString() : ''
    const selectedLocation =
        overview.locations.find(location => location.key === selectedLocationKey) || overview.locations[0]

    React.useEffect(() => {
        if (!overview.locations.length) {
            setSelectedLocationKey('')
            return
        }

        if (!overview.locations.some(location => location.key === selectedLocationKey)) {
            setSelectedLocationKey(overview.locations[0].key)
        }
    }, [overview.locations, selectedLocationKey])

    return (
        <Paper
            sx={{
                p: 0,
                borderRadius: '8px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)',
                overflow: 'hidden'
            }}
        >
            <Stack spacing={0}>
                <Stack
                    direction="row"
                    spacing={1.15}
                    alignItems="center"
                    sx={{
                        px: { xs: 1.4, md: 1.8 },
                        py: 1.55,
                        borderBottom: '1px solid rgba(129, 146, 178, 0.18)'
                    }}
                >
                    <PublicRoundedIcon sx={{ color: '#8fb4ff', fontSize: 25 }} />
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1.2rem', fontWeight: 900 }}>
                        {t('LIVE Castle')}
                    </Typography>
                </Stack>

                {updatedLabel ? (
                    <Typography sx={{ px: { xs: 1.4, md: 1.8 }, pt: 1.2, pb: 0.4, color: '#9fb0cc', fontSize: '0.82rem' }}>
                        {`${t('Updated')}: ${updatedLabel}`}
                    </Typography>
                ) : null}

                <Box
                    sx={{
                        px: { xs: 1.4, md: 1.8 },
                        pt: updatedLabel ? 0.6 : { xs: 1.4, md: 1.8 },
                        pb: { xs: 1.4, md: 1.8 },
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '270px minmax(0, 1fr)' },
                        alignItems: 'start',
                        gap: 1.35
                    }}
                >
                    {overview.locations.length ? (
                        <Stack
                            spacing={0.75}
                            sx={{
                                p: 0.9,
                                borderRadius: '8px',
                                border: '1px solid rgba(129, 146, 178, 0.18)',
                                backgroundColor: 'rgba(10, 19, 34, 0.46)',
                                maxHeight: { xs: 330, md: 'calc(100vh - 250px)' },
                                minHeight: { md: 236 },
                                overflowY: 'auto'
                            }}
                        >
                            {overview.locations.map(location => (
                                <CoreLocationListItem
                                    key={location.key}
                                    location={location}
                                    selected={location.key === selectedLocation?.key}
                                    onSelect={() => setSelectedLocationKey(location.key)}
                                    t={t}
                                />
                            ))}
                        </Stack>
                    ) : null}
                    {selectedLocation ? <CoreLocationCard location={selectedLocation} t={t} /> : null}
                </Box>
                {!overview.locations.length ? (
                    <Typography sx={{ px: { xs: 1.4, md: 1.8 }, pb: 1.8, color: '#9fb0cc', fontSize: '0.86rem', fontStyle: 'italic' }}>
                        {t('No troops or tools.')}
                    </Typography>
                ) : null}
            </Stack>
        </Paper>
    )
}

export default function BotSettingsPanel({ value, onChange, onImmediateSave, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [
        'Core Settings',
        'Calendar-based automation',
        'The calendar is informational. Event automation rules will be available in a future update; enabling this option does not control any bot module.',
        'Calendar automation is OFF. Events cannot start, stop or change bot modules.',
        'Relog Settings',
        'Relog Amount',
        'Relog Delay (seconds)',
        'Number of Times the bot will relog before it stops',
        'Delay in seconds before attempting to relog after getting kicked',
        'Session Handoff',
        'Enter Standby On Session Handoff',
        'If another manual game session replaces the bot, stop reconnect probes and wait in standby until you press Resume Bot.',
        'Session Handoff Reconnect (minutes)',
        'Used only when standby is enabled. 0 waits for Resume Bot; any value above 0 retries automatically after that many minutes.',
        'Manual only',
        'minute',
        'minutes',
        'Attack Time Settings',
        'Min Attack Wait (seconds)',
        'Max Attack Wait (seconds)',
        'Minimum wait time between attacks. Applied globally to all attack modules. Values lower than 6 seconds are blocked automatically.',
        'Maximum wait time between attacks. Applied globally to all attack modules.',
        'Daily attack totals sync automatically with the official GGE server after login and after successful hits.',
        'Error Cooldown Settings',
        'CRITICAL WARNING',
        'Keep these settings enabled! DO NOT change unless you know what you\'re doing! Modifying these settings can result in TEMPORARY ACCOUNT BANS! These settings protect your account from detection.',
        'Enable Error Cooldown',
        'Enable automatic cooldown after errors',
        'Error Threshold Count',
        'Number of errors before cooldown (1 or 2 is recommended)',
        'Error Threshold Minutes',
        'Time window for error counting (30 minutes is recommended)',
        'Cooldown Duration (minutes)',
        'Duration of cooldown period after getting Errors (30 minutes is recommended)'
    ])

    const updateValue = (key, nextValue) => {
        const nextSettings = {
            ...value,
            [key]: nextValue
        }

        onChange?.(nextSettings)

        return nextSettings
    }

    return (
        <Box
            sx={{
                pt: 0.25
            }}
        >
            <Stack spacing={2.4}>
                <Paper
                    sx={{
                        p: { xs: 2.2, md: 3.1 },
                        borderRadius: '8px',
                        border: '1px solid rgba(129, 146, 178, 0.18)',
                        background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                        boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
                    }}
                >
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={1.1} alignItems="center">
                            <CastleRoundedIcon sx={{ color: '#8fb4ff', fontSize: 25 }} />
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1.2rem', fontWeight: 900 }}>
                                {t('Core Settings')}
                            </Typography>
                        </Stack>

                        <SectionHeader title={t('Calendar-based automation')} />
                        <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Switch
                                    inputProps={{ 'aria-label': t('Calendar-based automation') }}
                                    checked={value.calendarAutomationEnabled === true}
                                    onChange={event => updateValue('calendarAutomationEnabled', event.target.checked)}
                                />
                                <Typography sx={{ fontWeight: 700 }}>{t('Calendar-based automation')}</Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#9fb0cc' }}>
                                {t(value.calendarAutomationEnabled === true
                                    ? 'The calendar is informational. Event automation rules will be available in a future update; enabling this option does not control any bot module.'
                                    : 'Calendar automation is OFF. Events cannot start, stop or change bot modules.')}
                            </Typography>
                        </Stack>

                        <SectionHeader title={t('Relog Settings')} />

                        <Grid container spacing={2.2}>
                            <Grid item xs={12} md={6}>
                                <SettingField
                                    label={t('Relog Amount')}
                                    value={value.relogAmount}
                                    onChange={nextValue => updateValue('relogAmount', nextValue)}
                                    helper={t('Number of Times the bot will relog before it stops')}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SettingField
                                    label={t('Relog Delay (seconds)')}
                                    value={value.relogDelaySeconds}
                                    onChange={nextValue => updateValue('relogDelaySeconds', nextValue)}
                                    helper={t('Delay in seconds before attempting to relog after getting kicked')}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <SectionHeader title={t('Session Handoff')} />

                        <Grid container spacing={2.2}>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <Switch
                                            checked={Boolean(value.enableStandbyAutoResume)}
                                            onChange={event => updateValue('enableStandbyAutoResume', event.target.checked)}
                                        />
                                        <Typography variant="body1" sx={{ color: '#f5f7ff', fontWeight: 700 }}>
                                            {t('Enter Standby On Session Handoff')}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                        <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#9fb0cc' }} />
                                        <Typography variant="caption" sx={{ color: '#9fb0cc', lineHeight: 1.45 }}>
                                            {t('If another manual game session replaces the bot, stop reconnect probes and wait in standby until you press Resume Bot.')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SettingField
                                    label={t('Session Handoff Reconnect (minutes)')}
                                    value={value.sessionHandoffReconnectMinutes}
                                    onChange={nextValue => {
                                        const nextSettings = updateValue('sessionHandoffReconnectMinutes', nextValue)
                                        onImmediateSave?.(nextSettings)
                                    }}
                                    helper={t('Used only when standby is enabled. 0 waits for Resume Bot; any value above 0 retries automatically after that many minutes.')}
                                    options={SESSION_HANDOFF_RECONNECT_OPTIONS.map(option => ({
                                        value: option,
                                        label: option === '0' ?
                                            `0 - ${t('Manual only')}` :
                                            `${option} ${t(option === '1' ? 'minute' : 'minutes')}`
                                    }))}
                                />
                            </Grid>
                        </Grid>

                        <SectionHeader title={t('Attack Time Settings')} />

                        <Stack direction="row" spacing={0.75} alignItems="flex-start">
                            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#9fb0cc' }} />
                            <Typography variant="caption" sx={{ color: '#9fb0cc', lineHeight: 1.45 }}>
                                {t('Daily attack totals sync automatically with the official GGE server after login and after successful hits.')}
                            </Typography>
                        </Stack>

                        <Grid container spacing={2.2}>
                            <Grid item xs={12} md={6}>
                                <SettingField
                                    label={t('Min Attack Wait (seconds)')}
                                    value={value.minAttackWaitSeconds}
                                    onChange={nextValue => updateValue('minAttackWaitSeconds', nextValue)}
                                    helper={t('Minimum wait time between attacks. Applied globally to all attack modules. Values lower than 6 seconds are blocked automatically.')}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SettingField
                                    label={t('Max Attack Wait (seconds)')}
                                    value={value.maxAttackWaitSeconds}
                                    onChange={nextValue => updateValue('maxAttackWaitSeconds', nextValue)}
                                    helper={t('Maximum wait time between attacks. Applied globally to all attack modules.')}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <SectionHeader title={t('Error Cooldown Settings')} />

                        <Stack spacing={2}>
                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#f6c455' }} />
                                <Typography variant="body1" sx={{ color: '#f8e7b2', lineHeight: 1.55 }}>
                                    <Box component="span" sx={{ color: '#f6c455', fontWeight: 700, mr: 0.75 }}>
                                        {t('CRITICAL WARNING')}
                                    </Box>
                                    {t('Keep these settings enabled! DO NOT change unless you know what you\'re doing! Modifying these settings can result in TEMPORARY ACCOUNT BANS! These settings protect your account from detection.')}
                                </Typography>
                            </Stack>

                            <Collapse in={Boolean(value.enableErrorCooldown)} timeout="auto" unmountOnExit>
                                <Grid container spacing={2.2} sx={{ pt: 0.5 }}>
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={0.5}>
                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                <Switch
                                                    checked={Boolean(value.enableErrorCooldown)}
                                                    onChange={event => updateValue('enableErrorCooldown', event.target.checked)}
                                                />
                                                <Typography variant="body1" sx={{ color: '#f5f7ff', fontWeight: 700 }}>
                                                    {t('Enable Error Cooldown')}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#9fb0cc' }} />
                                                <Typography variant="caption" sx={{ color: '#9fb0cc', lineHeight: 1.45 }}>
                                                    {t('Enable automatic cooldown after errors')}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <SettingField
                                            label={t('Error Threshold Count')}
                                            value={value.errorThresholdCount}
                                            onChange={nextValue => updateValue('errorThresholdCount', nextValue)}
                                            helper={t('Number of errors before cooldown (1 or 2 is recommended)')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <SettingField
                                            label={t('Error Threshold Minutes')}
                                            value={value.errorThresholdMinutes}
                                            onChange={nextValue => updateValue('errorThresholdMinutes', nextValue)}
                                            helper={t('Time window for error counting (30 minutes is recommended)')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <SettingField
                                            label={t('Cooldown Duration (minutes)')}
                                            value={value.cooldownDurationMinutes}
                                            onChange={nextValue => updateValue('cooldownDurationMinutes', nextValue)}
                                            helper={t('Duration of cooldown period after getting Errors (30 minutes is recommended)')}
                                        />
                                    </Grid>
                                </Grid>
                            </Collapse>

                            <Collapse in={!Boolean(value.enableErrorCooldown)} timeout="auto" unmountOnExit>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <Switch
                                            checked={Boolean(value.enableErrorCooldown)}
                                            onChange={event => updateValue('enableErrorCooldown', event.target.checked)}
                                        />
                                        <Typography variant="body1" sx={{ color: '#f5f7ff', fontWeight: 700 }}>
                                            {t('Enable Error Cooldown')}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                        <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#9fb0cc' }} />
                                        <Typography variant="caption" sx={{ color: '#9fb0cc', lineHeight: 1.45 }}>
                                            {t('Enable automatic cooldown after errors')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Collapse>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    )
}
